/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useMemo } from "react";
import Container from "@/src/components/Container/Container";
import LayoutPublicRoutes from "@/src/containers/Layouts/LayoutPublicRoutes";
import PartnerLogoSection from "@/src/components/Sections/Generic/PartnerLogoSection/PartnerLogoSection";
import LandingPageTeaser from "@/src/components/Teaser/LandingPageTeaser";
import AttorneySection from "@/src/components/Sections/Generic/AttorneySection/AttorneySection";
import FamilyLawConsultingCheckin from "@/src/components/Checkin/FamilyLawCheckin/Consulting/FamilyLawConsultingCheckin";
import { defaultProvenExpertTeaserCard } from "@/src/services/landingPageService";
import LinkedCardBox from "@/src/components/LinkedCardBox/LinkedCardBox";
import LpFamilyLawConsultingAdvoCardMeta from "./AdvoCard/LpFamilyLawConsultingAdvoCardMeta";
import LpFamilyLawConsultingAragMeta from "./Arag/LpFamilyLawConsultingAragMeta";
import LpFamilyLawConsultingDeuragMeta from "./Deurag/LpFamilyLawConsultingDeuragMeta";
import iconAttorneys from "@/src/assets/icons/generic/IconLawyers";
import iconDocument from "@/src/assets/icons/generic/IconDocument";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import HowItWorksSectionConsulting from "@/src/components/Sections/FamilyLaw/Consulting/HowItWorksSection/HowItWorksSectionConsulting";
import { PARTNER_IRIS } from "@/src/constants/partnerIris";
import LpFamilyLawConsultingLVMMeta from "./LVM/LpFamilyLawConsultingLVMMeta";
import LpFamilyLawConsultingAuxiliaMeta from "./Auxilia/LpFamilyLawConsultingAuxiliaMeta.tsx";

const getMetaDataByPartner = ({ partner }: { partner: string }) => {
  switch (partner) {
    case PARTNER_IRIS.ADVOCARD:
      return <LpFamilyLawConsultingAdvoCardMeta />;
    case PARTNER_IRIS.ARAG:
      return <LpFamilyLawConsultingAragMeta />;
    case PARTNER_IRIS.AUXILIA:
      return <LpFamilyLawConsultingAuxiliaMeta />;
    case PARTNER_IRIS.DEURAG:
      return <LpFamilyLawConsultingDeuragMeta />;
    case PARTNER_IRIS.LVM:
      return <LpFamilyLawConsultingLVMMeta />;
    default:
      return null;
  }
};

const LpFamilyLawConsultingPartner = ({ partner }: { partner: NonNullable<AbstractCase["acquisitionPartner"]> }) => {
  const { isLoggedIn } = useCustomer();
  const metaData = useMemo(() => getMetaDataByPartner({ partner }), [partner]);

  return (
    <LayoutPublicRoutes minimal={!isLoggedIn} clickableLogo={isLoggedIn}>
      {metaData}
      <LandingPageTeaser
        teaserButtonText={"Jetzt starten"}
        headline={partner === PARTNER_IRIS.AUXILIA ? "Professionelle Hilfe im Familien- und Erbrecht" : "Professionelle Hilfe im Familienrecht"}
        subheadline={"Kurz Anliegen schildern und Gespräch vereinbaren"}
        teaserButtonLink={isLoggedIn ? "/servicewelt" : "#top-checkin"}
      >
        <LinkedCardBox
          linkedCards={[
            defaultProvenExpertTeaserCard,
            {
              icon: iconAttorneys,
              heading: "Professionelle Beratung",
            },
            {
              icon: iconDocument,
              heading: "Unkomplizierte Abwicklung",
            },
          ]}
        />
      </LandingPageTeaser>
      <PartnerLogoSection partnerIri={partner} containerStyle={"settlementContainer"} />
      {!isLoggedIn && (
        <Container>
          <FamilyLawConsultingCheckin idPrefix={"top"} partner={partner} />
        </Container>
      )}
      <HowItWorksSectionConsulting />
      <AttorneySection
        subheading={"Sie möchten sich professionell und schnell unterstützen lassen? Wir helfen Ihnen - jederzeit und ortsunabhängig!"}
        heading={"Deutschlandweit tätige Partneranwälte"}
      />
      {!isLoggedIn && (
        <Container>
          <FamilyLawConsultingCheckin idPrefix={"top"} partner={partner} />
        </Container>
      )}
    </LayoutPublicRoutes>
  );
};

export default LpFamilyLawConsultingPartner;
